import React, { useMemo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  Clock,
  Code,
  Database,
  FileText,
  GitBranch,
  Globe,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';
import type { ActionKindId, FlowActionPersistedData } from '../actionWorkflowTypes';
import {
  actionBadgeLabel,
  buildActionPreviewRows,
  validateAction,
} from '../actionWorkflowTypes';
import './ActionNode.css';

export type ActionNodeData = FlowActionPersistedData & {
  incomingInputHint?: string;
};

const KIND_ICON: Record<ActionKindId, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>> =
  {
    'send-email': Mail,
    'send-slack': MessageSquare,
    'http-request': Globe,
    'update-crm': Database,
    'emit-event': Zap,
    'create-record': Plus,
    'delete-record': Trash2,
    logger: FileText,
    wait: Clock,
    'condition-check': GitBranch,
    'html-viewer': Code,
  };

export default function ActionNode({ data, selected }: NodeProps<ActionNodeData>) {
  const v = useMemo(() => validateAction(data), [data]);
  const rows = useMemo(() => buildActionPreviewRows(data.actionKind, data), [data]);
  const badge = actionBadgeLabel(data.actionKind, data);
  const Icon = KIND_ICON[data.actionKind];

  const statusClass =
    v.level === 'error'
      ? 'action-node__status--err'
      : v.level === 'warning'
        ? 'action-node__status--warn'
        : 'action-node__status--ok';
  const statusLine =
    v.level === 'error'
      ? `✗ Required: ${v.missingRequired.join(', ')}`
      : v.level === 'warning'
        ? `⚠ Optional: ${v.missingOptional.join(', ')} • ${v.varMappedCount} vars mapped`
        : `✓ Ready • ${v.varMappedCount} vars mapped`;

  const headerModifier = data.actionKind === 'emit-event' ? ' action-node__header--emit' : '';

  return (
    <div
      className={`action-node${selected ? ' action-node--selected' : ''}${v.level === 'error' ? ' action-node--invalid' : ''}${v.level === 'warning' && v.missingRequired.length === 0 ? ' action-node--warn' : ''}`}
      style={{ width: 180, minHeight: 100 }}
    >
      <Handle type="target" position={Position.Left} id="in" className="action-node__handle action-node__handle--target" />

      <div className={`action-node__header${headerModifier}`}>
        <span className="action-node__dot" aria-hidden />
        <Icon className="action-node__kind-icon" size={14} strokeWidth={2} aria-hidden />
        <div className="action-node__head-text">
          <div className="action-node__title">{data.label}</div>
        </div>
        <span className="action-node__badge" title="Action type">
          {badge}
        </span>
        {v.level === 'error' && (
          <span className="action-node__warn-dot" title="Required fields missing">
            !
          </span>
        )}
      </div>

      <div className="action-node__body nodrag">
        <div className="action-node__preview">
          {rows.map((row, i) => (
            <div key={i} className="action-node__preview-row">
              {row.k ? <span className="action-node__preview-k">{row.k}</span> : null}
              <span className="action-node__preview-v">{row.v}</span>
            </div>
          ))}
        </div>

        <div className={`action-node__status ${statusClass}`}>{statusLine}</div>
      </div>

      <Handle type="source" position={Position.Right} id="out" className="action-node__handle action-node__handle--source" />
    </div>
  );
}
